/** @graphql agg **/
import { useCallback, useEffect, useMemo, useState } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';

import type {
	useTeamDataBulkFetchQuery$data,
	useTeamDataBulkFetchQuery$variables,
} from './__generated__/useTeamDataBulkFetchQuery.graphql.ts';

// Re-export the generated types for external use
export type TeamQueryData = useTeamDataBulkFetchQuery$data;
export type TeamQueryVariables = useTeamDataBulkFetchQuery$variables;

// TeamV2 type derived from the generated query data
export type TeamV2 = NonNullable<NonNullable<TeamQueryData['team']>['teamsV2']>[number];

// TeamData type alias for backwards compatibility and external use
export type TeamData = TeamQueryData['team'];

export interface UseTeamDataResult {
	teamData: TeamData | null;
	isTeamDataLoading: boolean;
	teamDataError: Error | null;
	refetch: (variables?: TeamQueryVariables) => Promise<void>;
}

const UseTeamDataBulkFetchQuery = graphql`
	query useTeamDataBulkFetchQuery($teamIds: [ID!]!, $siteId: String!) {
		team {
			teamsV2(ids: $teamIds, siteId: $siteId) @optIn(to: "Team") {
				type {
					name
					verified
				}
				id
				displayName
				description
				largeAvatarImageUrl
			}
		}
	}
`;

export const useTeamData = (cloudId: string, teamIds: string[] = []): UseTeamDataResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Use a state with no setter to store teamIds for refetch callback
	const [ids] = useState(teamIds);

	const TEAMS_ANALYTICS_SOURCE = 'confluence.permissions-principals.teams';

	// Skip the initial query if there are no teamIds
	const skip = teamIds.length === 0;

	// Local state to store refetch result (since Apollo's useQuery doesn't update data when skip=true)
	const [refetchedData, setRefetchedData] = useState<TeamData | null>(null);
	const [isRefetching, setIsRefetching] = useState(false);
	const [refetchError, setRefetchError] = useState<Error | null>(null);

	const {
		data,
		loading: isQueryLoading,
		error: queryError,
		refetch: queryRefetch,
	} = useQueryInterop<TeamQueryData, TeamQueryVariables>(UseTeamDataBulkFetchQuery, {
		variables: { teamIds, siteId: cloudId },
		skip,
		client: getAGGClient(),
	});

	// Combine data from initial query and refetch
	// Prioritize refetched data over initial query data
	const teamData = useMemo(() => {
		if (refetchedData) {
			return refetchedData;
		}
		if (!data) {
			return null;
		}
		return data.team ?? null;
	}, [data, refetchedData]);

	const isTeamDataLoading = isQueryLoading || isRefetching;

	// Convert error to our expected format
	const teamDataError = useMemo(() => {
		const error = queryError || refetchError;
		if (error) {
			return error instanceof Error ? error : new Error(String(error));
		}
		return null;
	}, [queryError, refetchError]);

	// Fire analytics on error
	useEffect(() => {
		if (teamDataError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: TEAMS_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'fetchTeamData',
					attributes: {
						error: teamDataError,
						siteId: cloudId,
					},
				},
			}).fire();
		}
	}, [teamDataError, cloudId, createAnalyticsEvent]);

	// Refetch function that accepts optional variables
	const refetch = useCallback(
		async (variables?: TeamQueryVariables) => {
			const refetchVariables = variables ?? { teamIds: ids, siteId: cloudId };

			// Skip if no team IDs
			if (!refetchVariables.teamIds || refetchVariables.teamIds.length === 0) {
				return;
			}

			setIsRefetching(true);
			setRefetchError(null);

			try {
				const result = await queryRefetch(refetchVariables);
				// Store the refetch result in local state
				// The result structure depends on useQueryInterop implementation
				const resultData = (result as { data?: TeamQueryData })?.data?.team ?? null;
				setRefetchedData(resultData);
			} catch (error) {
				setRefetchError(error instanceof Error ? error : new Error(String(error)));
			} finally {
				setIsRefetching(false);
			}
		},
		[cloudId, queryRefetch, ids],
	);

	return {
		teamData,
		isTeamDataLoading,
		teamDataError,
		refetch,
	};
};
